import { useState, useRef, useEffect, useMemo } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Level Pro — supersedes the original Level. Six modes share a single
// DeviceOrientation listener; the user switches modes via tabs. Camera
// overlay mode adds a camera-permission flow on top of the motion one.
//
// Tilt values:
//   gamma: left-right [-90, 90], "x-tilt"
//   beta : front-back [-180, 180], "y-tilt"
//   alpha: compass-rotation [0, 360]
//
// Pitch (angle from horizontal when phone is held vertically against a
// surface) = abs(beta) for the inclinometer / slope / roof-pitch modes.
//
// Calibration: a single signed offset { dx, dy } subtracted from live
// (gamma, beta) before display. Persists in component state only —
// recalibration is intentionally per-session per the brief.

// V57.3 — Plumb mode cut (was awkward, niche use case). Remaining 5 modes:
// Bubble (along bottom edge), 2-Axis, Angle, Slope, Camera.
const MODES = [
  { id: "bubble", label: "Bubble" },
  { id: "twoaxis", label: "2-Axis" },
  { id: "angle", label: "Angle" },
  { id: "slope", label: "Slope" },
  { id: "camera", label: "Camera" },
];

export default function LevelPro() {
  const [state, setState] = useState("idle"); // motion permission state
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState("bubble");
  const [calib, setCalib] = useState({ dx: 0, dy: 0 });
  // V57.6: track screen orientation angle (0, 90, 180, -90/270) so the
  // bubble mode can pick the right sensor axis. Other modes (2-axis,
  // angle, slope, camera) read both axes and don't need this.
  const [screenAngle, setScreenAngle] = useState(0);
  const handler = useRef(null);
  const lastTilt = useRef({ x: 0, y: 0 });

  const enable = async () => {
    if (typeof DeviceOrientationEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { setState("denied"); return; }
      }
      handler.current = (e) => {
        const rawX = Math.max(-90, Math.min(90, e.gamma || 0));
        const rawY = Math.max(-180, Math.min(180, e.beta || 0));
        // V57.5 — low-pass filter on tilt values to remove sensor flutter.
        // EMA with alpha=0.3 — settles in ~100ms at 60Hz.
        const prev = lastTilt.current;
        const x = prev.x * 0.7 + rawX * 0.3;
        const y = prev.y * 0.7 + rawY * 0.3;
        lastTilt.current = { x, y };
        setTilt({ x, y });
      };
      window.addEventListener("deviceorientation", handler.current, true);
      setState("ok");
    } catch {
      setState("denied");
    }
  };

  useEffect(() => () => {
    if (handler.current) window.removeEventListener("deviceorientation", handler.current, true);
  }, []);

  // V57.6: listen for screen rotation. The screen.orientation API is
  // widely available since March 2023; we fall back to window.orientation
  // for older browsers (returns 0/90/-90/180).
  useEffect(() => {
    const readAngle = () => {
      if (screen.orientation && typeof screen.orientation.angle === "number") {
        setScreenAngle(screen.orientation.angle);
      } else if (typeof window.orientation === "number") {
        setScreenAngle(window.orientation);
      }
    };
    readAngle();
    if (screen.orientation && screen.orientation.addEventListener) {
      screen.orientation.addEventListener("change", readAngle);
      return () => screen.orientation.removeEventListener("change", readAngle);
    }
    window.addEventListener("orientationchange", readAngle);
    return () => window.removeEventListener("orientationchange", readAngle);
  }, []);

  const calibrate = () => {
    setCalib({ dx: lastTilt.current.x, dy: lastTilt.current.y });
  };
  const resetCalibration = () => setCalib({ dx: 0, dy: 0 });

  // Apply calibration to live values
  const cx = tilt.x - calib.dx;
  const cy = tilt.y - calib.dy;

  return (
    <ToolsShell title="Level Pro">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Tap Start to enable motion sensors — readings happen locally on your device.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Motion sensor" />
        </div>
      )}

      {state === "ok" && (
        <>
          {/* Mode tabs */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 18, justifyContent: "center" }}>
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                style={{
                  background: mode === m.id ? TOOLS_ACCENT : "#1C1E2E",
                  color: mode === m.id ? "#1A0604" : "#9A9DBE",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === "bubble" && <BubbleMode x={cx} y={cy} screenAngle={screenAngle} />}
          {mode === "twoaxis" && <TwoAxisMode x={cx} y={cy} />}
          {mode === "angle" && <AngleMode x={cx} y={cy} />}
          {mode === "slope" && <SlopeMode x={cx} y={cy} />}
          {mode === "camera" && <CameraMode x={cx} y={cy} />}

          {/* Calibration controls (not for camera mode) */}
          {mode !== "camera" && (
            <div style={{ marginTop: 28, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={calibrate}
                style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
              >
                Zero here (calibrate)
              </button>
              {(calib.dx !== 0 || calib.dy !== 0) && (
                <button
                  onClick={resetCalibration}
                  style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}
                >
                  Reset
                </button>
              )}
            </div>
          )}
        </>
      )}
    </ToolsShell>
  );
}

/* ---------- Mode 1: Bubble — orientation-aware ---------- */
// V57.6 — third rework. Key fix: V57.5 was reading gamma (left-right
// device tilt) regardless of screen orientation. When the phone rotates
// to landscape, gamma now refers to a DIFFERENT physical axis than the
// user expects — they want "is the bottom of what I'm looking at level?"
// which maps to a different sensor reading in each orientation.
//
// The fix is to pick the sensor axis based on screen.orientation.angle:
//
//   0    portrait, normal       → use gamma (left-right device tilt)
//   180  portrait, upside-down  → use gamma, negated (rare on phones)
//   90   landscape, left-side   → use beta (front-back), sign flipped
//   -90  landscape, right-side  → use beta, opposite sign
//
// The tube itself ALWAYS renders horizontally on screen (along the
// bottom edge of the visible viewport) because CSS coordinates rotate
// with the screen automatically. Only the *sensor axis* changes.
//
// Pattern cribbed from publicly-available web spirit level reference
// implementations (codepen.io/nitnelav/pen/KxYdqB and similar). The
// orientation-axis swap is the key insight I missed in V57.3/4/5.
//
// Bubble physics: floats AWAY from gravity toward the HIGH side, matching
// a real spirit level. When the right side tilts down, the bubble floats
// LEFT (toward the high side).
//
// Animation: 0.18s ease-out cubic-bezier paired with the upstream EMA
// (alpha=0.3) sensor smoothing in LevelPro's handler. Together these
// give a believable physical-fluid feel without measurable lag.
function BubbleMode({ x, y, screenAngle }) {
  // Pick the right axis for "is the bottom edge level".
  // Returns a value in [-90, 90] range representing tilt along the
  // visible-screen's horizontal axis.
  let edgeTilt;
  const angle = Math.round(screenAngle);
  if (angle === 90) {
    // Landscape, rotated CCW (left-side up). The screen's horizontal
    // is now the device's front-back axis. Beta is in [-180, 180];
    // for level use it's near 0 (lying flat) but we want "0 = level".
    edgeTilt = y;
  } else if (angle === -90 || angle === 270) {
    // Landscape, rotated CW. Beta direction is flipped relative to
    // the screen's visual horizontal.
    edgeTilt = -y;
  } else if (angle === 180) {
    // Upside-down portrait. Rare. Gamma inverted.
    edgeTilt = -x;
  } else {
    // Normal portrait (0). Gamma directly maps to screen-horizontal tilt.
    edgeTilt = x;
  }

  const level = Math.abs(edgeTilt) < 0.5;
  const range = 30; // ± degrees mapped to tube width
  const clamped = Math.max(-range, Math.min(range, edgeTilt));
  // Bubble floats to HIGH side → negate.
  const bubblePct = -(clamped / range) * 50;
  const BUBBLE_PX = 48;
  const TUBE_H = 56;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", minHeight: 240, paddingBottom: 18 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: level ? TOOLS_ACCENT : "#E8E9F3", fontVariantNumeric: "tabular-nums", marginBottom: 20 }}>
        {level ? "Level" : `${edgeTilt.toFixed(1)}°`}
      </div>
      <div style={{ fontSize: 11, color: "#5B5E7A", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.8 }}>
        Lay phone's long edge along the surface
      </div>
      <div style={{
        position: "relative",
        width: "100%",
        maxWidth: 420,
        height: TUBE_H,
        background: "#13141F",
        border: `2px solid ${level ? TOOLS_ACCENT : "#262839"}`,
        borderRadius: TUBE_H / 2,
      }}>
        {/* Center notch (target) */}
        <div style={{ position: "absolute", left: "50%", top: 6, bottom: 6, width: 2, background: "#262839", transform: "translateX(-1px)" }} />
        {/* Side ticks */}
        <div style={{ position: "absolute", left: "calc(50% - 22px)", top: 10, bottom: 10, width: 1.5, background: "#1F2030" }} />
        <div style={{ position: "absolute", left: "calc(50% + 22px)", top: 10, bottom: 10, width: 1.5, background: "#1F2030" }} />
        {/* Bubble */}
        <div style={{
          position: "absolute",
          left: `calc(50% + ${bubblePct}% - ${BUBBLE_PX / 2}px)`,
          top: `calc(50% - ${BUBBLE_PX / 2}px)`,
          width: BUBBLE_PX,
          height: BUBBLE_PX,
          borderRadius: "50%",
          background: level ? TOOLS_ACCENT : "#7C8099",
          // V57.7: removed CSS `left` transition. The 2-axis mode has
          // no position transition and feels smooth because position
          // updates 60Hz with EMA-smoothed sensor data are already
          // fluid. Adding an 0.18s transition on top caused every
          // sensor frame to start a new tween that the NEXT frame
          // would interrupt — net effect was sluggish lag. Trust the
          // upstream EMA, animate via direct position updates.
          transition: "background 0.2s",
          boxShadow: level ? `0 0 18px ${TOOLS_ACCENT}66` : "none",
        }} />
      </div>
      {/* V57.6: small orientation hint shown when phone is sideways, so
          user understands the level adapts to their phone position */}
      {(angle === 90 || angle === -90 || angle === 270) && (
        <div style={{ marginTop: 14, fontSize: 11, color: "#5B5E7A", textAlign: "center", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Landscape · long edge = bottom of screen
        </div>
      )}
    </div>
  );
}

/* ---------- Mode 2: Two-axis surface ---------- */
function TwoAxisMode({ x, y }) {
  const level = Math.abs(x) < 0.5 && Math.abs(y) < 0.5;
  const R = 130, dotR = 22;
  const range = 30;
  const dx = (Math.max(-range, Math.min(range, x)) / range) * (R - dotR);
  const dy = (Math.max(-range, Math.min(range, y)) / range) * (R - dotR);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ position: "relative", width: R * 2, height: R * 2, borderRadius: "50%", border: `2px solid ${level ? TOOLS_ACCENT : "#262839"}`, background: "#13141F", transition: "border-color 0.2s" }}>
        <div style={{ position: "absolute", left: "50%", top: 0, width: 1, height: "100%", background: "#262839" }} />
        <div style={{ position: "absolute", top: "50%", left: 0, height: 1, width: "100%", background: "#262839" }} />
        <div style={{ position: "absolute", left: "50%", top: "50%", width: 60, height: 60, marginLeft: -30, marginTop: -30, borderRadius: "50%", border: "1px dashed #2A2C3D" }} />
        <div style={{
          position: "absolute",
          left: "50%", top: "50%",
          width: dotR * 2, height: dotR * 2,
          marginLeft: -dotR, marginTop: -dotR,
          borderRadius: "50%",
          background: level ? TOOLS_ACCENT : "#F5A299",
          transform: `translate(${dx}px, ${dy}px)`,
          transition: "background 0.2s",
          boxShadow: level ? `0 0 16px ${TOOLS_ACCENT}66` : "none",
        }} />
      </div>
      <div style={{ marginTop: 22, fontSize: 22, fontWeight: 700, color: level ? TOOLS_ACCENT : "#E8E9F3", fontVariantNumeric: "tabular-nums" }}>
        {level ? "Level" : `${x.toFixed(1)}° × ${y.toFixed(1)}°`}
      </div>
      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 10, textAlign: "center", maxWidth: 300 }}>
        Place phone flat on the surface. Bubble shows tilt in both directions.
      </p>
    </div>
  );
}

/* ---------- Mode 3: Angle finder / inclinometer ---------- */
function AngleMode({ x, y }) {
  // For a phone held vertically against an edge, beta is the inclination.
  // For a phone held flat, the "angle of the surface" is also derived
  // from the larger-magnitude axis.
  const angle = Math.abs(y) > Math.abs(x) ? y : x;
  const display = Math.abs(angle);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg width="240" height="200" viewBox="0 0 240 200">
        {/* Baseline */}
        <line x1="20" y1="180" x2="220" y2="180" stroke="#262839" strokeWidth="2" />
        {/* Rotating arm */}
        <g transform={`rotate(${-angle} 30 180)`}>
          <line x1="30" y1="180" x2="220" y2="180" stroke={TOOLS_ACCENT} strokeWidth="3" strokeLinecap="round" />
          <circle cx="30" cy="180" r="6" fill={TOOLS_ACCENT} />
        </g>
        {/* Angle arc */}
        <path
          d={`M ${30 + 60} 180 A 60 60 0 0 0 ${30 + 60 * Math.cos(-angle * Math.PI / 180)} ${180 + 60 * Math.sin(-angle * Math.PI / 180)}`}
          fill="none" stroke="#F5A299" strokeWidth="1.5" strokeDasharray="3 3"
        />
      </svg>
      <div style={{ marginTop: 10, fontSize: 40, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>
        {display.toFixed(1)}°
      </div>
      <div style={{ fontSize: 12, color: "#9A9DBE", marginTop: 4 }}>
        from horizontal
      </div>
      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center", maxWidth: 300 }}>
        Hold phone against the surface you want to measure. Reading is the angle from horizontal.
      </p>
    </div>
  );
}

/* ---------- Mode 4: Slope / grade ---------- */
function SlopeMode({ x, y }) {
  const angleDeg = Math.abs(Math.abs(y) > Math.abs(x) ? y : x);
  // Percent slope = tan(angle) * 100
  const percent = Math.tan(angleDeg * Math.PI / 180) * 100;
  // Rise / run as pretty fraction: for any angle, run = 12 inches (standard),
  // rise = 12 * tan(angle). Round to nearest 0.5 in.
  const rise12 = 12 * Math.tan(angleDeg * Math.PI / 180);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg width="260" height="170" viewBox="0 0 260 170">
        <line x1="20" y1="150" x2="240" y2="150" stroke="#262839" strokeWidth="2" />
        <line x1="20" y1="150" x2="20" y2={150 - 80 * Math.sin(angleDeg * Math.PI / 180) * 1.5} stroke="#1F2030" strokeDasharray="2 4" />
        <g transform={`rotate(${-angleDeg} 20 150)`}>
          <line x1="20" y1="150" x2="220" y2="150" stroke={TOOLS_ACCENT} strokeWidth="3" strokeLinecap="round" />
          <circle cx="20" cy="150" r="6" fill={TOOLS_ACCENT} />
          <circle cx="220" cy="150" r="6" fill={TOOLS_ACCENT} />
        </g>
        <text x="130" y="160" fill="#7C8099" fontSize="11" textAnchor="middle">run</text>
        <text x="6" y="105" fill="#7C8099" fontSize="11">rise</text>
      </svg>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 18, width: "100%", maxWidth: 380 }}>
        <Readout label="Degrees" value={`${angleDeg.toFixed(1)}°`} />
        <Readout label="Percent" value={`${percent.toFixed(1)}%`} />
        <Readout label="Rise / 12" value={`${rise12.toFixed(1)}"`} />
      </div>
      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center", maxWidth: 320 }}>
        Common building slopes: 1% = drainage; 5% = wheelchair-accessible ramp max; 8.3% = ADA ramp limit; 33% = 18.4° (4/12 roof).
      </p>
    </div>
  );
}
/* ---------- Mode 6: Camera overlay ---------- */
function CameraMode({ x, y }) {
  const [camState, setCamState] = useState("idle"); // idle | requesting | ok | denied | unsupported
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const enableCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setCamState("unsupported"); return; }
    if (!window.isSecureContext) { setCamState("unsupported"); return; }
    setCamState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      // V57.6: srcObject attachment moved to post-render useEffect.
      // The <video> element is conditionally rendered (camState === "ok")
      // and isn't in the DOM at this point — assigning srcObject here
      // would silently no-op on a null ref.
      setCamState("ok");
    } catch (err) {
      if (err && err.name === "NotAllowedError") setCamState("denied");
      else if (err && err.name === "NotFoundError") setCamState("nodevices");
      else setCamState("denied");
    }
  };

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // V57.6: attach stream after the <video> element renders.
  useEffect(() => {
    if (camState === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [camState]);

  if (camState !== "ok") {
    return (
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
          Camera overlay shows a level line through your live camera view — useful for hanging things by eye. Tap to enable.
        </p>
        <button onClick={enableCamera} style={primaryBtn}>Enable camera</button>
        <SensorNotice state={camState === "idle" || camState === "requesting" ? "prompt" : camState} what="Camera" />
      </div>
    );
  }

  // The horizontal reference line stays horizontal in world-space; in the
  // phone's frame it appears to rotate by -gamma (left-right tilt).
  const rotation = -x;
  const level = Math.abs(x) < 0.5 && Math.abs(y) < 0.5;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ position: "relative", width: "100%", maxWidth: 480, aspectRatio: "3 / 4", background: "#000", borderRadius: 12, overflow: "hidden" }}>
        <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        {/* Horizontal reference line */}
        <div style={{
          position: "absolute",
          top: "50%",
          left: "10%",
          width: "80%",
          height: 2,
          background: level ? TOOLS_ACCENT : "#FFFFFFCC",
          transform: `translateY(-1px) rotate(${rotation}deg)`,
          transition: "background 0.2s",
          boxShadow: level ? `0 0 12px ${TOOLS_ACCENT}` : "0 0 8px #00000088",
        }} />
        {/* Vertical reference */}
        <div style={{
          position: "absolute",
          left: "50%",
          top: "10%",
          height: "80%",
          width: 2,
          background: "#FFFFFF66",
          transform: "translateX(-1px)",
        }} />
        {/* Readout overlay */}
        <div style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0, 0, 0, 0.7)",
          color: level ? TOOLS_ACCENT : "#fff",
          padding: "6px 14px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}>
          {level ? "Level" : `${x.toFixed(1)}°`}
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 12, textAlign: "center", maxWidth: 300 }}>
        White line shows world-horizontal through the live camera. When the line goes flat across the frame, the camera is level.
      </p>
    </div>
  );
}

/* ---------- Helper ---------- */
function Readout({ label, value }) {
  return (
    <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#7C8099", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
